import { useState, useEffect } from "react";
import { 
  Clock, 
  Send, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  AlertCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "./ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { cn } from "../lib/utils";

// Status configuration matching backend
const STATUS_CONFIG = {
  pending: {
    label: "Wird gesendet",
    labelShort: "Ausstehend",
    color: "gray",
    badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
    icon: Clock,
    isSuccess: false,
    isError: false,
    isWarning: false
  },
  sent: {
    label: "An Resend übergeben",
    labelShort: "Übergeben",
    color: "blue",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    icon: Send,
    isSuccess: false, // NOT success - just handed off!
    isError: false,
    isWarning: true
  },
  delivered: {
    label: "Zugestellt",
    labelShort: "Zugestellt",
    color: "green",
    badgeClass: "bg-green-100 text-green-700 border-green-200",
    icon: CheckCircle,
    isSuccess: true, // ONLY delivered is true success
    isError: false,
    isWarning: false
  },
  delivery_delayed: {
    label: "Zustellung verzögert",
    labelShort: "Verzögert",
    color: "yellow",
    badgeClass: "bg-yellow-100 text-yellow-700 border-yellow-200",
    icon: Clock,
    isSuccess: false,
    isError: false,
    isWarning: true
  },
  bounced: {
    label: "Nicht zustellbar",
    labelShort: "Bounced",
    color: "red",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    icon: XCircle,
    isSuccess: false,
    isError: true,
    isWarning: false
  },
  failed: {
    label: "Versand fehlgeschlagen",
    labelShort: "Fehlgeschlagen",
    color: "red",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    icon: AlertTriangle,
    isSuccess: false,
    isError: true,
    isWarning: false
  },
  complained: {
    label: "Als Spam markiert",
    labelShort: "Spam",
    color: "orange",
    badgeClass: "bg-orange-100 text-orange-700 border-orange-200",
    icon: AlertCircle,
    isSuccess: false,
    isError: true,
    isWarning: false
  },
  opened: {
    label: "Geöffnet",
    labelShort: "Geöffnet",
    color: "purple",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-200",
    icon: Mail,
    isSuccess: true,
    isError: false,
    isWarning: false
  },
  clicked: {
    label: "Link geklickt",
    labelShort: "Geklickt",
    color: "indigo",
    badgeClass: "bg-indigo-100 text-indigo-700 border-indigo-200",
    icon: ExternalLink,
    isSuccess: true,
    isError: false,
    isWarning: false
  }
};

// Helper to format date
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return isoString;
  }
};

/**
 * Email Status Badge - Simple badge showing delivery status
 */
export function EmailStatusBadge({ 
  status, 
  showLabel = true,
  size = "default" // "small" | "default"
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  const sizeClasses = size === "small" 
    ? "text-xs px-1.5 py-0.5" 
    : "text-sm px-2 py-1";
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={cn(
              config.badgeClass, 
              sizeClasses,
              "inline-flex items-center gap-1 font-medium"
            )}
          >
            <Icon className={size === "small" ? "h-3 w-3" : "h-4 w-4"} />
            {showLabel && (
              <span>{size === "small" ? config.labelShort : config.label}</span>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.label}</p>
          {config.isWarning && status === "sent" && (
            <p className="text-xs text-muted-foreground mt-1">
              E-Mail wurde an Resend übergeben, aber noch nicht bestätigt zugestellt.
            </p>
          )}
          {config.isError && (
            <p className="text-xs text-red-500 mt-1">
              Die E-Mail konnte nicht zugestellt werden.
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Email Delivery Alert - Warning banner for failed emails
 */
export function EmailDeliveryAlert({ 
  email,
  onRetry,
  className 
}) {
  const config = STATUS_CONFIG[email?.send_status] || STATUS_CONFIG.pending;
  
  if (!config.isError) return null;
  
  return (
    <div className={cn(
      "rounded-lg border p-4",
      email.send_status === "complained" 
        ? "bg-orange-50 border-orange-200" 
        : "bg-red-50 border-red-200",
      className
    )}>
      <div className="flex items-start gap-3">
        <config.icon className={cn(
          "h-5 w-5 mt-0.5",
          email.send_status === "complained" ? "text-orange-500" : "text-red-500"
        )} />
        <div className="flex-1">
          <h4 className={cn(
            "font-medium",
            email.send_status === "complained" ? "text-orange-800" : "text-red-800"
          )}>
            {config.label}
          </h4>
          <p className={cn(
            "text-sm mt-1",
            email.send_status === "complained" ? "text-orange-700" : "text-red-700"
          )}>
            E-Mail an <strong>{email.to_email}</strong> konnte nicht zugestellt werden.
          </p>
          {(email.bounce_reason || email.error_message) && (
            <p className="text-sm mt-2 text-gray-600">
              <strong>Grund:</strong> {email.bounce_reason || email.error_message}
            </p>
          )}
          {email.bounce_type && (
            <p className="text-xs mt-1 text-gray-500">
              Bounce-Typ: {email.bounce_type}
              {email.bounce_subtype && ` (${email.bounce_subtype})`}
            </p>
          )}
          <p className="text-sm mt-3 text-gray-600">
            <strong>Empfehlung:</strong> E-Mail-Adresse prüfen oder Kunden alternativ kontaktieren.
          </p>
        </div>
        {onRetry && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRetry}
            className="shrink-0"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Erneut senden
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Email Tracking Card - Detailed view of email delivery status
 */
export function EmailTrackingCard({ 
  email,
  showDetails = false,
  onRetry,
  className 
}) {
  const [isOpen, setIsOpen] = useState(showDetails);
  const config = STATUS_CONFIG[email?.send_status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  if (!email) return null;
  
  return (
    <div className={cn(
      "rounded-lg border bg-card",
      config.isError && "border-red-200",
      config.isSuccess && "border-green-200",
      className
    )}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "rounded-full p-2",
                config.isError && "bg-red-100",
                config.isSuccess && "bg-green-100",
                config.isWarning && "bg-yellow-100",
                !config.isError && !config.isSuccess && !config.isWarning && "bg-gray-100"
              )}>
                <Icon className={cn(
                  "h-5 w-5",
                  config.isError && "text-red-600",
                  config.isSuccess && "text-green-600",
                  config.isWarning && "text-yellow-600",
                  !config.isError && !config.isSuccess && !config.isWarning && "text-gray-600"
                )} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{email.subject}</span>
                  <EmailStatusBadge status={email.send_status} size="small" showLabel={false} />
                </div>
                <p className="text-sm text-muted-foreground">
                  An: {email.to_email}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {formatDateTime(email.send_status_updated_at || email.created_at)}
              </span>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
          
          {/* Show alert for errors */}
          {config.isError && (
            <div className="mt-3 p-3 rounded bg-red-50 text-red-700 text-sm">
              <strong>{config.label}:</strong> {email.bounce_reason || email.error_message || "E-Mail konnte nicht zugestellt werden."}
            </div>
          )}
        </div>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t">
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">
                  <EmailStatusBadge status={email.send_status} />
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Provider ID:</span>
                <p className="font-mono text-xs mt-1 truncate">
                  {email.provider_email_id || "-"}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Erstellt:</span>
                <p className="mt-1">{formatDateTime(email.created_at)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Letztes Update:</span>
                <p className="mt-1">{formatDateTime(email.send_status_updated_at)}</p>
              </div>
              {email.delivered_at && (
                <div>
                  <span className="text-muted-foreground">Zugestellt am:</span>
                  <p className="mt-1 text-green-600 font-medium">
                    {formatDateTime(email.delivered_at)}
                  </p>
                </div>
              )}
              {email.bounce_type && (
                <div>
                  <span className="text-muted-foreground">Bounce-Typ:</span>
                  <p className="mt-1 text-red-600">
                    {email.bounce_type}
                    {email.bounce_subtype && ` (${email.bounce_subtype})`}
                  </p>
                </div>
              )}
            </div>
            
            {(email.bounce_reason || email.error_message) && (
              <div className="mt-4">
                <span className="text-muted-foreground text-sm">Fehlermeldung:</span>
                <p className="mt-1 p-2 bg-gray-50 rounded text-sm font-mono">
                  {email.bounce_reason || email.error_message}
                </p>
              </div>
            )}
            
            {onRetry && config.isError && (
              <div className="mt-4 flex justify-end">
                <Button variant="outline" size="sm" onClick={onRetry}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Erneut senden
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/**
 * Email Tracking List - List of emails for an entity
 */
export function EmailTrackingList({ 
  emails = [],
  loading = false,
  onRefresh,
  emptyMessage = "Keine E-Mails gesendet",
  className 
}) {
  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (emails.length === 0) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center py-8 text-muted-foreground",
        className
      )}>
        <Mail className="h-8 w-8 mb-2 opacity-50" />
        <p>{emptyMessage}</p>
      </div>
    );
  }
  
  return (
    <div className={cn("space-y-3", className)}>
      {onRefresh && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Aktualisieren
          </Button>
        </div>
      )}
      {emails.map((email) => (
        <EmailTrackingCard key={email.id} email={email} />
      ))}
    </div>
  );
}

/**
 * Email Delivery Stats - Summary statistics
 */
export function EmailDeliveryStats({ stats, className }) {
  if (!stats) return null;
  
  const items = [
    { label: "Gesamt", value: stats.total, color: "text-gray-700" },
    { label: "Zugestellt", value: stats.delivered, color: "text-green-600" },
    { label: "Ausstehend", value: stats.pending + stats.sent, color: "text-blue-600" },
    { label: "Fehlgeschlagen", value: stats.bounced + stats.failed, color: "text-red-600" },
  ];
  
  return (
    <div className={cn(
      "grid grid-cols-4 gap-4 p-4 rounded-lg bg-card border",
      className
    )}>
      {items.map((item) => (
        <div key={item.label} className="text-center">
          <p className={cn("text-2xl font-bold", item.color)}>{item.value}</p>
          <p className="text-sm text-muted-foreground">{item.label}</p>
        </div>
      ))}
      {stats.delivery_rate !== undefined && (
        <div className="col-span-4 pt-3 border-t mt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Zustellrate:</span>
            <span className={cn(
              "font-medium",
              stats.delivery_rate >= 95 && "text-green-600",
              stats.delivery_rate >= 80 && stats.delivery_rate < 95 && "text-yellow-600",
              stats.delivery_rate < 80 && "text-red-600"
            )}>
              {stats.delivery_rate}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default {
  EmailStatusBadge,
  EmailDeliveryAlert,
  EmailTrackingCard,
  EmailTrackingList,
  EmailDeliveryStats
};
